import React, { useContext, useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Button, MenuItem, Box, 
  Card, CardContent, Alert, Select, FormHelperText,
  CircularProgress, FormControl, InputLabel, TextField,
  Stepper, Step, StepLabel, StepContent, Divider
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { bunkeringApi } from '../api/bunkering';
import apiClient from '../api/apiClient';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export default function UploadBunkering() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [availableVessels, setAvailableVessels] = useState([]);
  const [vesselLoading, setVesselLoading] = useState(true);
  const [vesselError, setVesselError] = useState('');

  const [activeStep, setActiveStep] = useState(0);
  const [vesselImo, setVesselImo] = useState('');
  const [bunkeringDate, setBunkeringDate] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdBunkering, setCreatedBunkering] = useState(null);

  // Check if user can upload
  const canUpload = user && (user.role === 'SHIPOWNER' || user.role === 'SUPPLIER');

  useEffect(() => {
    if (canUpload) {
      fetchVessels();
    }
  }, [canUpload]);

  const fetchVessels = async () => {
    setVesselLoading(true);
    setVesselError('');
    try {
      // Shipowners list their vessels; suppliers list via the supplier-specific endpoint
      const endpoint = user.role === 'SUPPLIER'
        ? '/vessels/supplier'
        : '/vessels';
      const response = await apiClient.get(endpoint);
      setAvailableVessels(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch vessels:", err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setVesselError(p.detail || p.title || 'Failed to load available vessels.');
      } else {
        setVesselError('Failed to load available vessels.');
      }
    } finally {
      setVesselLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (selectedFile.type !== 'application/pdf') {
        setUploadError('Please select a PDF file.');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        setUploadError('File size must be less than 10MB.');
        return;
      }

      setFile(selectedFile);
      setUploadError('');
    }
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!vesselImo || !file) {
      setUploadError('Please select a vessel and BDN file.');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const response = await bunkeringApi.createBunkering(
        vesselImo,
        file,
        bunkeringDate || null
      );
      
      setCreatedBunkering(response.data.data);
      setSuccess(true);
      
    } catch (err) {
      console.error('Upload failed:', err);
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        setUploadError(errorData.detail || errorData.title || 'Upload failed. Please try again.');
      } else {
        setUploadError('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const getSelectedVessel = () => {
    return availableVessels.find(v => v.imo === vesselImo);
  };

  const formatFileSize = (bytes) => {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Redirect if user doesn't have permission
  if (!canUpload) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You do not have permission to create bunkering events.
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            component={RouterLink}
            to="/bunkerings"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Bunkering Events
          </Button>
        </Box>
      </Container>
    );
  }

  const steps = [
    {
      label: 'Select Vessel',
      content: (
        <Box>
          {vesselLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : vesselError ? (
            <Alert severity="error">{vesselError}</Alert>
          ) : (
            <FormControl fullWidth required>
              <InputLabel id="vessel-select-label">Select Vessel</InputLabel>
              <Select
                id="vessel-select"
                labelId="vessel-select-label"
                value={vesselImo}
                label="Select Vessel"
                onChange={(e) => setVesselImo(e.target.value)}
              >
                {availableVessels.map((vessel) => (
                  <MenuItem key={vessel.imo} value={vessel.imo}>
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        {vessel.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        IMO: {vessel.imo}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose the vessel for this bunkering event
              </FormHelperText>
            </FormControl>
          )}
        </Box>
      )
    },
    {
      label: 'Set Bunkering Date (Optional)',
      content: (
        <Box>
          <TextField
            label="Bunkering Date"
            type="date"
            value={bunkeringDate}
            onChange={(e) => setBunkeringDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to use current date"
          />
        </Box>
      )
    },
    {
      label: 'Upload BDN File',
      content: (
        <Box>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            sx={{ mb: 2 }}
          >
            Choose BDN PDF File
          </Button>
          
          {file && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Selected: {file.name} ({formatFileSize(file.size)})
            </Alert>
          )}
          
          <Typography variant="caption" color="text.secondary" display="block">
            • Maximum file size: 10MB<br/>
            • Only PDF files are accepted<br/>
            • This will be the primary BDN document for the bunkering event
          </Typography>
        </Box>
      )
    },
    {
      label: 'Review & Submit',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Review Bunkering Event Details
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DirectionsBoatIcon />
                <Typography variant="subtitle1" fontWeight="500">
                  Vessel Information
                </Typography>
              </Box>
              
              {getSelectedVessel() && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {getSelectedVessel().name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>IMO:</strong> {getSelectedVessel().imo}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="body2">
                  <strong>Bunkering Date:</strong> {bunkeringDate || 'Current date'}
                </Typography>
              </Box>
              
              {file && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>BDN File:</strong> {file.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>File Size:</strong> {formatFileSize(file.size)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
          
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
        </Box>
      )
    }
  ];

  if (success && createdBunkering) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            
            <Typography variant="h4" gutterBottom>
              Bunkering Event Created Successfully!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your BDN has been uploaded and submitted to the blockchain for verification.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate(`/bunkerings/${createdBunkering.id}`)}
              >
                View Bunkering Details
              </Button>
              
              <Button
                variant="outlined"
                component={RouterLink}
                to="/bunkerings"
              >
                Back to Bunkering Events
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Create Another
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/bunkerings"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Bunkering Events
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <UploadFileIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Create New Bunkering Event
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Upload a Bunker Delivery Note (BDN) to create a new bunkering event
        </Typography>
      </Box>

      {/* Stepper */}
      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.content}
                  
                  <Box sx={{ mt: 3 }}>
                    {index === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={uploading || !vesselImo || !file}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                      >
                        {uploading ? 'Creating Bunkering Event...' : 'Create Bunkering Event'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={
                          (index === 0 && !vesselImo) ||
                          (index === 2 && !file)
                        }
                      >
                        Continue
                      </Button>
                    )}
                    
                    {index > 0 && (
                      <Button
                        onClick={handleBack}
                        sx={{ ml: 1 }}
                        disabled={uploading}
                      >
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>
    </Container>
  );
}